import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Reveal } from "@/components/Reveal";
import { TRADE_LANE_PINS } from "@/constants/options";

const KINGSTON = TRADE_LANE_PINS.find((p) => p.id === "kingston");
const SATELLITES = TRADE_LANE_PINS.filter((p) => p.id !== "kingston");
const BOUNDS = TRADE_LANE_PINS.map((p) => [p.lat, p.lon]);

const createPinIcon = (color, size, testId) =>
  L.divIcon({
    className: "ferguson-map-pin",
    html: `<span data-testid="${testId}" style="display:block;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 2px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.4);"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

const KINGSTON_ICON = createPinIcon("#D4AF37", 20, "trade-pin-kingston");
const SATELLITE_ICONS = SATELLITES.reduce((acc, pin) => {
  acc[pin.id] = createPinIcon("#2F6FD1", 15, `trade-pin-${pin.id}`);
  return acc;
}, {});

const tooltipText = (pin) =>
  pin.id === "kingston" ? pin.tooltip : `${pin.name} — Coming Soon — ${pin.lane}`;

function MapController({ mapRef, flyTarget }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    map.fitBounds(BOUNDS, { padding: [36, 36] });
  }, [map, mapRef]);

  useEffect(() => {
    if (flyTarget) {
      map.flyTo([flyTarget.lat, flyTarget.lon], 6, { duration: 1 });
    }
  }, [flyTarget, map]);

  return null;
}

export const TradeLanesMap = () => {
  const mapRef = useRef(null);
  const [selectedPin, setSelectedPin] = useState(KINGSTON);
  const [flyTarget, setFlyTarget] = useState(null);

  const handlePinClick = (pin) => {
    setSelectedPin(pin);
    setFlyTarget(pin);
  };

  const handleResetView = () => {
    setSelectedPin(KINGSTON);
    setFlyTarget(null);
    mapRef.current?.fitBounds(BOUNDS, { padding: [36, 36] });
  };

  return (
    <section id="trade-lanes" className="py-20 lg:py-28 bg-cream-alt" data-testid="trade-lanes-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10">
        <Reveal>
          <p className="label-eyebrow text-gold-muted mb-3">Active &amp; Expanding Corridors</p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-ink max-w-2xl">
            Where We Move Cargo
          </h2>
          <p className="mt-3 text-base text-ink-soft max-w-xl">
            Click a pin to see the corridor, its status, and a live view of the port below.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 sm:mt-14">
          <div
            className="isolate rounded-2xl border border-ink/10 bg-white p-2 sm:p-3 shadow-sm overflow-hidden"
            data-testid="trade-lanes-map-container"
          >
            <div className="h-[380px] sm:h-[480px] lg:h-[540px] rounded-xl overflow-hidden">
              <MapContainer
                center={[KINGSTON.lat, KINGSTON.lon]}
                zoom={3}
                scrollWheelZoom={false}
                worldCopyJump
                style={{ height: "100%", width: "100%", background: "#EFEBDF" }}
                data-testid="trade-lanes-leaflet-map"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapController mapRef={mapRef} flyTarget={flyTarget} />

                {SATELLITES.map((pin) => (
                  <Polyline
                    key={`line-${pin.id}`}
                    positions={[
                      [KINGSTON.lat, KINGSTON.lon],
                      [pin.lat, pin.lon],
                    ]}
                    pathOptions={{
                      color: "#D4AF37",
                      weight: 1.5,
                      opacity: 0.6,
                      dashArray: "6 8",
                      className: "trade-corridor-line",
                    }}
                  />
                ))}

                {SATELLITES.map((pin) => (
                  <Marker
                    key={pin.id}
                    position={[pin.lat, pin.lon]}
                    icon={SATELLITE_ICONS[pin.id]}
                    eventHandlers={{ click: () => handlePinClick(pin) }}
                  >
                    <Popup>
                      <div
                        className="text-xs font-sans text-ink max-w-[220px]"
                        data-testid={`trade-pin-popup-${pin.id}`}
                      >
                        <span className="inline-block text-[9px] font-mono uppercase tracking-widest text-ocean bg-ocean/10 rounded-full px-2 py-0.5 mb-1">
                          Coming Soon
                        </span>
                        <p className="font-semibold text-ink">{pin.name}</p>
                        <p className="text-ink-soft mt-0.5">{pin.lane}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                <Marker
                  position={[KINGSTON.lat, KINGSTON.lon]}
                  icon={KINGSTON_ICON}
                  zIndexOffset={1000}
                  eventHandlers={{ click: () => handlePinClick(KINGSTON) }}
                >
                  <Popup>
                    <div
                      className="text-xs font-sans text-ink max-w-[240px]"
                      data-testid="trade-pin-popup-kingston"
                    >
                      <span className="inline-block text-[9px] font-mono uppercase tracking-widest text-gold-muted bg-gold/15 rounded-full px-2 py-0.5 mb-1">
                        Headquarters
                      </span>
                      <p className="font-semibold text-ink">{tooltipText(KINGSTON)}</p>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-6 text-xs text-ink-faint">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-gold inline-block" /> Headquarters
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-ocean inline-block" /> Coming Soon
            </span>
          </div>

          <div
            className="mt-8 rounded-2xl border border-ink/10 bg-white shadow-sm overflow-hidden"
            data-testid="live-port-view-panel"
          >
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/10">
              <div>
                <p className="label-eyebrow text-gold-muted mb-1">Live Port View</p>
                <div className="flex items-center gap-2" data-testid="live-port-view-label">
                  <span className="font-display font-semibold text-ink text-sm sm:text-base">
                    {selectedPin.name}
                  </span>
                  <span
                    className={`inline-block text-[9px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5 ${
                      selectedPin.id === "kingston"
                        ? "text-gold-muted bg-gold/15"
                        : "text-ocean bg-ocean/10"
                    }`}
                  >
                    {selectedPin.id === "kingston" ? "Headquarters" : "Coming Soon"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleResetView}
                data-testid="live-port-view-reset-btn"
                className="shrink-0 text-xs font-display font-medium text-ink-soft hover:text-gold-muted border border-ink/15 hover:border-gold/40 rounded-full px-3.5 py-1.5 transition-colors"
              >
                Reset to HQ
              </button>
            </div>
            <iframe
              key={selectedPin.id}
              title={`Live map view of ${selectedPin.name}`}
              data-testid="live-port-view-iframe"
              src={`https://www.google.com/maps?q=${selectedPin.lat},${selectedPin.lon}&z=13&output=embed`}
              width="100%"
              height="320"
              style={{ border: 0 }}
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
